import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import {
  Col,
  Row,
  Button,
  Label,
  Input,
  Spinner,
  Modal,
  FormText,
  ModalHeader,
  ModalBody,
  Table,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Badge,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import axios from "axios";
import { epcToEAN13, validateEAN13 } from "../../../../utils/ean13Generator";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

function Escaneos() {
  const { user } = useContext(AuthContext);
  const scanInputRef = useRef(null);

  // API URLs
  const URL_ARTICULOS = import.meta.env.VITE_URL_ARTICULOS;
  const URL_ARTICULOS_PROPORCION =
    import.meta.env.VITE_URL_ARTICULOS_PROPORCION;
  const URL_ESCANEOS = import.meta.env.VITE_URL_ESCANEOS;
  const URL_AREAS = import.meta.env.VITE_URL_AREAS;
  const URL_ARTICULOS_ESCANEADOS =
    import.meta.env.VITE_URL_ARTICULOS_ESCANEADOS;

  // Current date
  let date = new Date();
  let hoy = moment(date).format("YYYY-MM-DD");

  // State variables
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Articulos states
  const [articulos, setArticulos] = useState([]);
  const [idsArticulos, setIdsArticulos] = useState([]);
  const [eanArticulos, setEanArticulos] = useState([]);

  // Escaneos states
  const [areas, setAreas] = useState([]);
  const [activeEscaneo, setActiveEscaneo] = useState(null);
  const [escaneosList, setEscaneosList] = useState([]);
  const [newEscaneoName, setNewEscaneoName] = useState("");

  // Scanned items states
  const [inputFields, setInputFields] = useState([]);
  const [manualInput, setManualInput] = useState({
    id: uuidv4(),
    articulos: "",
  });

  // History states
  const [historyEscaneos, setHistoryEscaneos] = useState([]);
  const [selectedHistoryEscaneo, setSelectedHistoryEscaneo] = useState(null);
  const [lastScannedItem, setLastScannedItem] = useState({
    show: false,
    success: false,
  });
  const [historyItems, setHistoryItems] = useState([]);

  // Report states
  const [reportData, setReportData] = useState([]);
  const [selectedReportEscaneo, setSelectedReportEscaneo] = useState(null);

  // Load all necessary data on component mount
  useEffect(() => {
    loadAreas();
    loadEscaneos();
  }, []);

  // Fetch and cache all articulos proporcion
  useMemo(() => {
    setLoading(true);
    axios
      .get(`${URL_ARTICULOS_PROPORCION}Activos`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        const allArticulos = res.data;
        setArticulos(allArticulos);
        let result = allArticulos.map((a) => a._id);
        setIdsArticulos(result);
        let result2 = allArticulos.map((a) => (a.EAN ? a.EAN.toUpperCase() : a.EAN));
        setEanArticulos(result2);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los artículos",
        });
      });
  }, []);

  // Load areas
  const loadAreas = () => {
    setLoading(true);
    axios
      .get(`${URL_AREAS}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        setAreas(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar las áreas",
        });
      });
  };

  // Load escaneos
  const loadEscaneos = () => {
    setLoading(true);
    axios
      .get(`${URL_ESCANEOS}/Activos`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        setEscaneosList(res.data);
        setHistoryEscaneos(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los escaneos",
        });
      });
  };

  // Function to create a new escaneo
  const createEscaneo = () => {
    if (!newEscaneoName) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Por favor ingrese un nombre para el escaneo",
      });
      return;
    }

    setLoading(true);

    const newEscaneo = {
      name: newEscaneoName,
      areas: user.areas,
    };

    axios
      .post(`${URL_ESCANEOS}`, newEscaneo, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Escaneo creado correctamente",
          timer: 1500,
        });
        setActiveEscaneo(res.data);
        setNewEscaneoName("");
        setModalOpen(false);
        loadEscaneos();
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo crear el escaneo",
        });
      });
  };

  // Select an existing escaneo
  const selectEscaneo = (escaneo) => {
    // Check if the escaneo is marked as not editable
    if (escaneo.estado === "Cerrado") {
      Swal.fire({
        icon: "warning",
        title: "Escaneo no editable",
        text: "Este escaneo ha sido finalizado y no puede ser modificado.",
      });
      return;
    }

    setActiveEscaneo(escaneo);
    // Clear previous input fields
    setInputFields([]);
    // Load previously scanned items if any
    loadScannedItems(escaneo._id);

    // Focus on scan input after a short delay
    setTimeout(() => {
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    }, 300);
  };

  // Load already scanned items for an escaneo
  const loadScannedItems = (escaneoId) => {
    setLoading(true);
    axios
      .get(`${URL_ARTICULOS_ESCANEADOS}ByEscaneo/${escaneoId}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        // Transform the API response to match our input fields format
        const transformedItems = res.data.map((item) => ({
          id: item._id,
          idArticuloProporcion: item.articulosProporcion[0]._id,
          cantidad: item.cantidad,
          // Get visual information from the cached articulos
          ...getVisualInfoForArticulo(item.articulosProporcion[0]._id),
        }));

        setInputFields(transformedItems);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudieron cargar los artículos escaneados",
        });
      });
  };

  // Helper to get visual info for an article
  const getVisualInfoForArticulo = (articuloProporcionId) => {
    const articuloProporcion = articulos.find(
      (a) => a._id === articuloProporcionId
    );

    if (!articuloProporcion) {
      return {
        codigo: "N/A",
        nombre: "Artículo Desconocido",
        colorNombre: "N/A",
        tallaNombre: "N/A",
      };
    }

    const { articulos: articulosArray, colores, tallas } = articuloProporcion;

    const firstArticulo =
      articulosArray && articulosArray.length > 0 ? articulosArray[0] : null;
    const firstColor = colores && colores.length > 0 ? colores[0] : null;
    const firstTalla = tallas && tallas.length > 0 ? tallas[0] : null;

    return {
      codigo: firstArticulo ? firstArticulo.codigo : "N/A",
      nombre: firstArticulo ? firstArticulo.nombre : "Artículo Desconocido",
      colorNombre: firstColor ? firstColor.name : "N/A",
      tallaNombre: firstTalla ? firstTalla.name : "N/A",
    };
  };

  // ── Disparo de la lectura ─────────────────────────────────────────────────
  // Antes se buscaba en CADA tecla: con un lector que teclea caracter por caracter
  // el primer caracter ya daba "Articulo no encontrado" y borraba el campo. Ahora
  // la lectura se dispara con Enter/Tab, al completarse un EPC RFID, o al dejar de
  // llegar caracteres (lectores sin sufijo / pegado), y el campo se limpia antes.
  const scanTimerRef = useRef(null);
  const dispararEscaneo = (inputEl) => {
    clearTimeout(scanTimerRef.current);
    const raw = (inputEl.value || "").trim();
    if (!raw) return;
    inputEl.value = "";
    // El lector RFID puede mandar el EPC SGTIN-96 (24 hex) o su EAN-13 (13 digitos)
    const codigo = epcToEAN13(raw) || raw;
    buscarArticuloSingular(manualInput.id, { target: { value: codigo } });
  };
  const onChangeEscaneo = (event) => {
    const inputEl = event.target;
    clearTimeout(scanTimerRef.current);
    const hex = inputEl.value.replace(/\s+/g, "").toUpperCase();
    if (/^30[0-9A-F]{22}$/.test(hex)) {
      dispararEscaneo(inputEl);
      return;
    }
    scanTimerRef.current = setTimeout(() => dispararEscaneo(inputEl), 300);
  };
  const onKeyDownEscaneo = (event) => {
    if (
      event.key === "Enter" ||
      (event.key === "Tab" && event.target.value.trim())
    ) {
      event.preventDefault();
      dispararEscaneo(event.target);
    }
  };

  // Function to handle scanning an article
  async function buscarArticuloSingular(id, event) {
    // Validate event
    if (!event || !event.target) {
      console.error("Invalid event object");
      return;
    }

    const scanValue = event.target.value.toUpperCase();

    // Validate scan value
    if (!scanValue || scanValue.length < 1) {
      return;
    }

    // Make sure we have an active escaneo
    if (!activeEscaneo) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Debe seleccionar o crear un escaneo primero",
      });
      event.target.value = "";
      return;
    }

    try {
      // Check if the scanned value exists in the cached EAN codes
      // Coincide con el QR/EAN de la proporcion o con el RFID (EAN-13) de la etiqueta
      const index = eanArticulos.indexOf(scanValue);
      const porRFID =
        index === -1 && validateEAN13(scanValue)
          ? articulos.find((a) => a.RFID === scanValue)
          : null;
      if (index !== -1 || porRFID) {
        // Get the corresponding article proportion ID
        const articuloProporcionId = porRFID ? porRFID._id : idsArticulos[index];

        // Get the full article proportion data for visual info
        const articuloProporcion = articulos.find(
          (a) => a._id === articuloProporcionId
        );

        if (!articuloProporcion) {
          throw new Error("Article not found in cached data");
        }

        const {
          articulos: articulosArray,
          colores,
          tallas,
        } = articuloProporcion;

        const firstArticulo =
          articulosArray && articulosArray.length > 0
            ? articulosArray[0]
            : null;
        const firstColor = colores && colores.length > 0 ? colores[0] : null;
        const firstTalla = tallas && tallas.length > 0 ? tallas[0] : null;

        if (!firstArticulo || !firstColor || !firstTalla) {
          throw new Error("Incomplete article data");
        }

        // Check if the item is already in the input fields
        const existingItem = inputFields.find(
          (item) => item.idArticuloProporcion === articuloProporcionId
        );

        if (existingItem) {
          // If it exists, increment the count locally
          const updatedFields = inputFields.map((field) => {
            if (field.idArticuloProporcion === articuloProporcionId) {
              return {
                ...field,
                cantidad: field.cantidad + 1,
              };
            }
            return field;
          });

          setInputFields(updatedFields);

          // Also update in the database
          const updateData = {
            escaneos: activeEscaneo._id,
            articulosProporcion: articuloProporcionId,
            cantidad: 1, // The backend will increment this
          };

          axios
            .post(`${URL_ARTICULOS_ESCANEADOS}`, updateData, {
              headers: {
                Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
              },
            })
            .catch((err) => {
              console.error("Error updating scan count:", err);
            });
        } else {
          // If it doesn't exist, add a new entry with count 1 and visual info
          const newField = {
            id: uuidv4(),
            idArticuloProporcion: articuloProporcionId,
            cantidad: 1,
            // Visual information
            codigo: firstArticulo.codigo,
            nombre: firstArticulo.nombre,
            colorNombre: firstColor.name,
            tallaNombre: firstTalla.name,
          };

          setInputFields((prev) => [...prev, newField]);

          // Also add to the database
          const newData = {
            escaneos: [activeEscaneo._id],
            articulosProporcion: [articuloProporcionId],
            cantidad: 1,
          };

          axios
            .post(`${URL_ARTICULOS_ESCANEADOS}`, newData, {
              headers: {
                Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
              },
            })
            .catch((err) => {
              console.error("Error creating new scan record:", err);
            });
        }

        // Show success feedback for a short time
        setLastScannedItem({ show: true, success: true });
        setTimeout(
          () => setLastScannedItem({ show: false, success: false }),
          1500
        );
      } else {
        // If not found in cached data, show error
        throw new Error("Article not found in cached data");
      }
    } catch (error) {
      console.error("Error:", error);
      // Toast: un modal le quita el foco al campo y las lecturas siguientes del
      // lector se pierden.
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: `Artículo no encontrado: ${scanValue}`,
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
    }
  }

  // Function to finish scanning session
  const finishScanning = () => {
    Swal.fire({
      title: "¿Finalizar escaneo?",
      text: "¿Está seguro de que desea finalizar este escaneo?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, finalizar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        setActiveEscaneo(null);
        setInputFields([]);
        Swal.fire(
          "¡Finalizado!",
          "El escaneo ha sido finalizado correctamente.",
          "success" 
        );
      }
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    });
  };

  // Function to load history for a specific escaneo
  const loadEscaneoHistory = (escaneoId) => {
    setLoading(true);
    setSelectedHistoryEscaneo(escaneoId);

    axios
      .get(`${URL_ARTICULOS_ESCANEADOS}ByEscaneo/${escaneoId}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        setHistoryItems(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar el historial de escaneo",
        });
      });
  };

  // Function to load report data for a specific escaneo
  const loadEscaneoReport = (escaneoId) => {
    setLoading(true);
    setSelectedReportEscaneo(escaneoId);

    axios
      .get(`${URL_ARTICULOS_ESCANEADOS}Reporte/${escaneoId}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        setReportData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo cargar el reporte de escaneo",
        });
      });
  };

  // Function to export report to CSV
  const exportReportToCSV = () => {
    if (!reportData || reportData.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No hay datos para exportar",
      });
      return;
    }

    // Create CSV content
    let csvContent = "Código,Descripción,Cantidad\n";

    reportData.forEach((item) => {
      csvContent += `${item.codigo},${item.descripcion},${item.totalCantidad}\n`;
    });

    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `reporte-escaneo-${selectedReportEscaneo}.csv`
    );
    document.body.appendChild(link);

    // Trigger download
    link.click();

    // Clean up
    document.body.removeChild(link);
  };

  // Reset function for a new scanning session
  const resetAndStartNew = () => {
    setActiveEscaneo(null);
    setInputFields([]);
    setModalOpen(true);
  };

  return (
    <div>
      <Header />
      <br />
      <br />{" "}
      <div className="container">
        <div className="row mb-4"></div>

        {loading && (
          <div className="text-center">
            <Spinner color="primary" />
            <p>Cargando datos...</p>
          </div>
        )}

        {!activeEscaneo ? (
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs={4} className="d-flex">
                  <Button
                    size="sm"
                    href="/MenuTiendas"
                    className="btn btn-danger"
                    id="botonRegresar"
                  >
                    Regresar
                  </Button>
                </Col>
                <Col xs={4} className="text-center">
                  <h1 className="m-0">Escaneos de Artículos</h1>
                </Col>
                <Col xs={4}></Col>
              </Row>
              <br />
              <br />
              <div className="d-flex justify-content-center align-items-center">
                <h3>Seleccione o cree un escaneo</h3>
              </div>
              <div className="d-flex justify-content-center align-items-center">
                <div>
                  {" "}
                  <Button
                    color="primary"
                    onClick={() => setModalOpen(true)}
                  >
                    {" "}
                    Nuevo Escaneo{" "}
                  </Button>{" "}
                  {/* <Button
                    color="info"
                    onClick={() => setHistoryModalOpen(true)}
                  >
                    {" "}
                    Historial{" "}
                  </Button>{" "} */}
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {escaneosList.length > 0 ? (
                <FormGroup>
                  <Col md={4}>
                  <Label for="escaneoSelect">
                    <strong>Seleccione un escaneo</strong>
                  </Label>
                  <Input
                    type="select"
                    name="escaneoSelect"
                    id="escaneoSelect"
                    onChange={(e) => {
                      const selectedEscaneo = escaneosList.find(
                        (escaneo) => escaneo._id === e.target.value
                      );
                      if (selectedEscaneo) {
                        selectEscaneo(selectedEscaneo);
                      }
                    }}
                    value={activeEscaneo ? activeEscaneo._id : ""}
                  >
                    <option value="" disabled>Selecciona</option>
                    {escaneosList.map((escaneo) => (
                      <option
                        key={escaneo._id}
                        value={escaneo._id}
                        disabled={escaneo.estado === "Cerrado"}
                      >
                        {escaneo.estado === "Cerrado" ? "🔒 " : ""}
                        {escaneo.name} -{" "}
                        {escaneo.areas && escaneo.areas.length > 0
                          ? `Áreas: ${escaneo.areas
                              .map((a) => a.name)
                              .join(", ")}`
                          : "Sin áreas"}{" "}
                        - {moment(escaneo.createdAt).format("DD/MM/YYYY HH:mm")}
                      </option>
                    ))}
                  </Input>
                  </Col>
                  <FormText>
                    Seleccione un escaneo existente o cree uno nuevo para
                    comenzar a escanear artículos.
                  </FormText>
                </FormGroup>
              ) : (
                <div className="text-center p-3">
                  <p>
                    No hay escaneos disponibles. Cree uno nuevo para comenzar.
                  </p>
                </div>
              )}
              
            </CardBody>
          </Card>
        ) : (
          <div>
            <div className="mb-4">
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h3>Escaneo: {activeEscaneo.name}</h3>
                    <div>
                      <Button
                        color="danger"
                        onClick={finishScanning}
                      >
                        Finalizar Escaneo
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <p>
                    <strong>Áreas:</strong>{" "}
                    {activeEscaneo.areas && activeEscaneo.areas.length > 0
                      ? Array.isArray(activeEscaneo.areas[0])
                        ? activeEscaneo.areas.map((a) => a.name).join(", ")
                        : areas
                            .filter((a) => activeEscaneo.areas.includes(a._id))
                            .map((a) => a.name)
                            .join(", ")
                      : "Sin áreas asignadas"}
                  </p>
                  <p>
                    <strong>Fecha de inicio:</strong>{" "}
                    {moment(activeEscaneo.createdAt).format("DD/MM/YYYY HH:mm")}
                  </p>
                </CardBody>
              </Card>
            </div>

            <div className="mb-4">
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h3>Escanear Artículos</h3>
                    <div>
                      <h4>
                        <div
                          className="total-count-badge"
                          style={{ fontSize: "1.4rem", padding: "8px 16px" }}
                        >
                          <Badge
                            color="success"
                            pill
                            className="p-2"
                            style={{ fontSize: "1.2rem" }}
                          >
                            <strong>Total: </strong>
                            {inputFields.reduce(
                              (total, item) => total + item.cantidad,
                              0
                            )}
                            <strong> piezas</strong>
                          </Badge>
                        </div>
                      </h4>
                    </div>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="scanInput">
                          <strong>Escanear código</strong>
                        </Label>
                        <div className="scan-input-container position-relative">
                          <Input
                            id="scanInput"
                            ref={scanInputRef}
                            name="articulos"
                            type="string"
                            placeholder="Escanee el código de barras..."
                            autoComplete="off"
                            onChange={onChangeEscaneo}
                            onKeyDown={onKeyDownEscaneo}
                          />
                          {lastScannedItem.show && (
                            <div
                              className={`scan-feedback position-absolute ${
                                lastScannedItem.success
                                  ? "text-success"
                                  : "text-danger"
                              }`}
                              style={{
                                top: "50%",
                                right: "15px",
                                transform: "translateY(-50%)",
                                zIndex: 100,
                                transition: "opacity 0.3s ease",
                              }}
                            >
                              {lastScannedItem.success ? (
                                <i className="fa fa-check-circle fa-2x animate__animated animate__fadeIn"></i>
                              ) : (
                                <i className="fa fa-times-circle fa-2x animate__animated animate__fadeIn"></i>
                              )}
                            </div>
                          )}
                        </div>
                        <small className="text-muted">
                          Escanee el código de barras o ingréselo manualmente y
                          presione Enter
                        </small>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <div
                        className="last-scanned-container p-2 border rounded"
                        style={{ minHeight: "100px" }}
                      >
                        <h5>Último artículo escaneado:</h5>
                        {inputFields.length > 0 && (
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>
                                {inputFields[inputFields.length - 1].codigo}
                              </strong>
                              <p className="mb-0">
                                {inputFields[inputFields.length - 1].nombre}
                              </p>
                              <small className="text-muted">
                                {
                                  inputFields[inputFields.length - 1]
                                    .colorNombre
                                }{" "}
                                /{" "}
                                {
                                  inputFields[inputFields.length - 1]
                                    .tallaNombre
                                }
                              </small>
                            </div>
                            <Badge
                              color="primary"
                              pill
                              className="px-3 py-2"
                              style={{
                                fontSize: "1.2rem",
                                fontWeight: "bold",
                                minWidth: "50px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              {inputFields[inputFields.length - 1].cantidad}
                            </Badge>
                          </div>
                        )}
                        {inputFields.length === 0 && (
                          <p className="text-muted">
                            No se ha escaneado ningún artículo
                          </p>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </div>

            <div>
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h3>Artículos Escaneados</h3>
                    <Badge
                              color="primary"
                              pill
                              className="px-3 py-2"
                              style={{
                                fontSize: "1.2rem",
                                fontWeight: "bold",
                                minWidth: "50px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              Total: {inputFields.length} 
                            </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  {inputFields.length > 0 ? (
                    <Table responsive striped>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Código</th>
                          <th>Artículo</th>
                          <th>Color</th>
                          <th>Talla</th>
                          <th>Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inputFields.map((field, index) => (
                          <tr key={field.id}>
                            <td>{index + 1}</td>
                            <td>{field.codigo}</td>
                            <td>{field.nombre}</td>
                            <td>{field.colorNombre}</td>
                            <td>{field.tallaNombre}</td>
                            <td>{field.cantidad}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-center p-5">
                      <p>No hay artículos escaneados aún.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        )}

        {/* Modal for creating a new escaneo */}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
          <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
            Crear Nuevo Escaneo
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="escaneoName">Nombre del Escaneo</Label>
              <Input
                id="escaneoName"
                type="text"
                value={newEscaneoName}
                onChange={(e) => setNewEscaneoName(e.target.value)}
                placeholder="Ingrese un nombre descriptivo"
              />
            </FormGroup>

            <FormGroup>
              <Label for="escaneoAreas">Áreas</Label>
              <Input
                id="escaneoAreas"
                type="select"
                disabled
                value={user.areaName}
              >
                <option>{user.areaName}</option>
              </Input>
            </FormGroup>

            <div className="d-flex justify-content-end mt-4">
              <Button
                color="danger"
                onClick={() => setModalOpen(false)}
                className="mr-2"
              >
                Cancelar
              </Button>
              <Button color="success" onClick={createEscaneo}>
                Crear Escaneo
              </Button>
            </div>
          </ModalBody>
        </Modal>
        {/* Modal for viewing history */}
        <Modal
          isOpen={historyModalOpen}
          toggle={() => setHistoryModalOpen(!historyModalOpen)}
          size="lg"
        >
          <ModalHeader toggle={() => setHistoryModalOpen(!historyModalOpen)}>
            Historial de Escaneos
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={4}>
                <h5>Escaneos</h5>
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <ListGroup>
                    {historyEscaneos.map((escaneo) => (
                      <ListGroupItem
                        key={escaneo._id}
                        active={selectedHistoryEscaneo === escaneo._id}
                        onClick={() => loadEscaneoHistory(escaneo._id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div>{escaneo.name}</div>
                        <small>
                          {moment(escaneo.createdAt).format("DD/MM/YYYY HH:mm")}
                        </small>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </div>
              </Col>
              <Col md={8}>
                <h5>Artículos Escaneados</h5>
                {selectedHistoryEscaneo ? (
                  historyItems.length > 0 ? (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Artículo</th>
                            <th>Color</th>
                            <th>Talla</th>
                            <th>Cantidad</th>
                            <th>Fecha</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyItems.map((item, index) => (
                            <tr key={item._id}>
                              <td>{index + 1}</td>
                              <td>
                                {
                                  getVisualInfoForArticulo(
                                    item.articulosProporcion[0]._id
                                  ).codigo
                                }{" "}
                                -{" "}
                                {
                                  getVisualInfoForArticulo(
                                    item.articulosProporcion[0]._id
                                  ).nombre
                                }{" "}
                              </td>
                              <td>
                                {
                                  getVisualInfoForArticulo(
                                    item.articulosProporcion[0]._id
                                  ).colorNombre
                                }
                              </td>
                              <td>
                                {
                                  getVisualInfoForArticulo(
                                    item.articulosProporcion[0]._id
                                  ).tallaNombre
                                }
                              </td>
                              <td>{item.cantidad}</td>
                              <td>
                                {moment(item.createdAt).format(
                                  "DD/MM/YYYY HH:mm"
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center p-5">
                      <p>No hay artículos escaneados para este escaneo.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center p-5">
                    <p>Seleccione un escaneo para ver los detalles.</p>
                  </div>
                )}
              </Col>
            </Row>
          </ModalBody>
        </Modal>

        {/* Modal for viewing reports */}
        <Modal
          isOpen={reportModalOpen}
          toggle={() => setReportModalOpen(!reportModalOpen)}
          size="lg"
        >
          <ModalHeader toggle={() => setReportModalOpen(!reportModalOpen)}>
            Reportes de Escaneos
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={4}>
                <h5>Escaneos</h5>
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <ListGroup>
                    {historyEscaneos.map((escaneo) => (
                      <ListGroupItem
                        key={escaneo._id}
                        active={selectedReportEscaneo === escaneo._id}
                        onClick={() => loadEscaneoReport(escaneo._id)}
                        style={{ cursor: "pointer" }}
                      >
                        <div>{escaneo.name}</div>
                        <small>
                          {moment(escaneo.createdAt).format("DD/MM/YYYY HH:mm")}
                        </small>
                      </ListGroupItem>
                    ))}
                  </ListGroup>
                </div>
              </Col>
              <Col md={8}>
                <h5>Reporte de Artículos</h5>
                {selectedReportEscaneo ? (
                  <div>
                    <div className="mb-3 text-right">
                      <Button color="success" onClick={exportReportToCSV}>
                        Exportar a CSV
                      </Button>
                    </div>

                    {reportData.length > 0 ? (
                      <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        <Table responsive striped>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Código</th>
                              <th>Artículo</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.map((item, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{item.codigo}</td>
                                <td>{item.descripcion}</td>
                                <td>
                                  <strong>{item.totalCantidad}</strong>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center p-5">
                        <p>No hay datos disponibles para este reporte.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center p-5">
                    <p>Seleccione un escaneo para ver el reporte.</p>
                  </div>
                )}
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
}

export default Escaneos;
